import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';

// rxjs
import { forkJoin, Subscription, take } from 'rxjs';
import { filter, switchMap } from "rxjs/operators";

// Services
import { UserProfileService } from '../../services/user-profile.service';
import { ChatBotService } from '@my7n/ui';
import { GlobalAppConfigFacadeService } from '../../services/facades/global-app-config-facade.service';
import { CvListService } from "../../services/cv/cv-list.service";
import { CvParseFacadeService } from "../../services/facades/cv-parse.facade.service";

// Interfaces
import { IUserPreferences } from '../../interfaces/user-preferences';
import { ICv } from "../../interfaces/cv";
import { PARSE_STATE } from "../../interfaces/cv-parse";

@Component({
    selector: 'welcome-page',
    templateUrl: './welcome-page.component.html',
    styleUrls: ['./welcome-page.component.scss'],
    standalone: false
})
export class WelcomePageComponent implements OnInit, OnDestroy {
  loading = false;
  Agreement = [];
  ratio = 566 / 1008;

  user: IUserPreferences;
  primaryCv = signal<ICv>(null);
  parsingInProgress = signal(false);

  subscriptions$: Subscription = new Subscription();

  constructor(private router: Router,
    private userProfileService: UserProfileService,
    private globalAppConfigFacadeService: GlobalAppConfigFacadeService,
    private chatBotService: ChatBotService,
    private cvListService: CvListService,
    private cvParseFacadeService: CvParseFacadeService
  ) { }

  ngOnInit() {
    // On init check once if user has primary cv
    forkJoin({
      user: this.globalAppConfigFacadeService.user$.pipe(take(1)),
      primaryCv: this.cvListService.getPrimaryCv()
    }).subscribe(results => {
      this.user = results.user;
      this.primaryCv.set(results.primaryCv);
    });

    this.handleActiveParsing();

    this.chatBotService.changeMessage({
      messageHTMLString: 'We look forward to serving you as your agent. We help the best IT specialists to get interesting jobs and build satisfying carriers. Create your CV to help us in that process.'
    });
  }

  handleActiveParsing() {
    // Check if active parsing job is running
    this.subscriptions$.add(
      this.cvParseFacadeService.parsingState$.pipe(
        filter((state) => state === PARSE_STATE.PARSING)
      ).subscribe(() => this.parsingInProgress.set(true))
    );

    // If active parsing job is finished, refresh info about primary CV
    this.subscriptions$.add(
      this.cvParseFacadeService.parsingState$.pipe(
        filter((state) => state === PARSE_STATE.PARSED_SUCCESS),
        switchMap(() => this.cvListService.getPrimaryCv()),
      ).subscribe((cv: ICv) => {
        this.parsingInProgress.set(false);

        if (cv) {
          this.primaryCv.set(cv);
        }
      })
    );
  }

  letMeIn() {
    this.loading = true;

    // Redirection url depends on whether user has primary CV or not
    const redirectUrl = this.primaryCv() || this.parsingInProgress() ? '/cv/uploading' : '/cv/upload';

    this.userProfileService.updateWelcomeScreenVisit(true).then(() => {
      this.router.navigateByUrl(redirectUrl).then(() => console.debug(`[WelcomePageComponent] Redirected to ${redirectUrl}`));
      this.loading = false;
    });
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
  }
}
